<script lang="ts">
/** 通讯录的模块级缓存:同一页挂几个选人框也只拉一次 */
export interface PickablePerson {
  id: number;
  displayName: string;
  username: string | null;
  orgName: string | null;
}
let cache: PickablePerson[] | null = null;
</script>

<script setup lang="ts">
/**
 * 选人:搜索(姓名/工号/部门)→ 点选。硬规则:只能选到系统账号,
 * 不提供自由文本——没账号的人进不了工作台,派了活也是黑洞。
 * 数据源 GET /people。
 */
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';

const props = withDefaults(defineProps<{
  modelValue: number[];
  multiple?: boolean;
  placeholder?: string;
  excludeIds?: number[];
}>(), {
  multiple: true,
  placeholder: '搜索姓名 / 工号 / 部门',
  excludeIds: () => [],
});
const emit = defineEmits<{ 'update:modelValue': [value: number[]] }>();

const people = ref<PickablePerson[]>([]);
const query = ref('');
const open = ref(false);
const loadError = ref('');

onMounted(async () => {
  if (cache) {
    people.value = cache;
    return;
  }
  try {
    const result = await eqsApi.people();
    const raw = (result as { people?: unknown }).people;
    cache = Array.isArray(raw) ? (raw as PickablePerson[]) : [];
    people.value = cache;
  } catch {
    loadError.value = '通讯录加载失败,稍后重试';
  }
});

const selected = computed(() =>
  props.modelValue
    .map((id) => people.value.find((person) => person.id === id))
    .filter((person): person is PickablePerson => Boolean(person)));

const matches = computed(() => {
  const text = query.value.trim().toLowerCase();
  const excluded = new Set([...props.modelValue, ...props.excludeIds]);
  return people.value
    .filter((person) => !excluded.has(person.id))
    .filter((person) => !text
      || person.displayName.toLowerCase().includes(text)
      || (person.username ?? '').toLowerCase().includes(text)
      || (person.orgName ?? '').toLowerCase().includes(text))
    .slice(0, 12);
});

function pick(person: PickablePerson): void {
  const next = props.multiple ? [...props.modelValue, person.id] : [person.id];
  emit('update:modelValue', next);
  query.value = '';
  if (!props.multiple) open.value = false;
}

function remove(id: number): void {
  emit('update:modelValue', props.modelValue.filter((value) => value !== id));
}

/** 点选项会先触发 blur:延迟关闭,让 click 有机会命中 */
function closeSoon(): void {
  window.setTimeout(() => { open.value = false; }, 160);
}
</script>

<template>
  <div class="people-picker">
    <div v-if="selected.length" class="picker-chips">
      <span v-for="person in selected" :key="person.id" class="picker-chip">
        {{ person.displayName }}<template v-if="person.orgName"> · {{ person.orgName }}</template>
        <button type="button" :aria-label="`移除 ${person.displayName}`" @click="remove(person.id)">×</button>
      </span>
    </div>
    <input
      v-model="query"
      type="search"
      :placeholder="loadError || placeholder"
      :disabled="Boolean(loadError)"
      @focus="open = true"
      @blur="closeSoon"
    >
    <ul v-if="open && matches.length" class="picker-menu" role="listbox">
      <li v-for="person in matches" :key="person.id">
        <button type="button" @mousedown.prevent="pick(person)">
          <strong>{{ person.displayName }}</strong>
          <span>{{ [person.username, person.orgName].filter(Boolean).join(' · ') }}</span>
        </button>
      </li>
    </ul>
    <p v-else-if="open && query.trim()" class="picker-empty">
      查无此人——只能选到系统账号;没账号的同事请先找管理员开号。
    </p>
  </div>
</template>

<style scoped>
.people-picker { position: relative; display: grid; gap: 6px; }
.people-picker input { width: 100%; }
.picker-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.picker-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px;
  border: 1px solid var(--line-strong); border-radius: 999px;
  background: var(--white); font-size: 12px; font-weight: 700;
}
.picker-chip button {
  border: 0; background: transparent; color: var(--muted);
  font-size: 14px; line-height: 1; cursor: pointer; padding: 0 2px;
}
.picker-chip button:hover { color: var(--risk); }
.picker-menu {
  position: absolute; z-index: 30; top: 100%; left: 0; right: 0; margin: 4px 0 0; padding: 4px;
  list-style: none; background: var(--white); border: 1px solid var(--line-strong);
  border-radius: var(--radius-s); box-shadow: 0 12px 30px rgba(15, 43, 82, .14);
  max-height: 280px; overflow: auto;
}
.picker-menu button {
  display: flex; width: 100%; align-items: baseline; gap: 8px; padding: 7px 9px;
  border: 0; border-radius: 6px; background: transparent; cursor: pointer; text-align: left;
}
.picker-menu button:hover { background: rgba(0, 117, 174, .08); }
.picker-menu strong { font-size: 13px; }
.picker-menu span { color: var(--muted); font-size: 12px; }
.picker-empty { margin: 4px 0 0; color: var(--muted); font-size: 12px; }
</style>
